import { KatModal } from '@amzn/katal-react';
import React, { useEffect } from 'react';
import { ParentEntityProvider } from 'src/contexts/ParentEntityContext';
import { useLearningModule } from 'src/hooks';
import { EntityType } from 'src/constants/entity';
import { getStatusCodeFromError } from 'src/utils/error';
import { useAppSelector } from 'src/redux/hooks';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';
import LearningModuleContentView from './LearningModuleContentView';
import LoadingView from '../LoadingView';
import ErrorView from '../ErrorView';

export interface LearningModuleModalProps {
  learningModuleId: string;
  onModalClose?: () => void;
  parentId?: string;
  parentType?: EntityType;
  unauthenticated?: boolean;
  clientId?: string;
  /**
   * When true, indicates the modal is rendered via createPortal into a parent document.
   * This affects which document.body is manipulated for scroll control.
   */
  isPortalRendered?: boolean;
}

interface ModalWrapperProps {
  title?: string;
  onModalClose?: () => void;
  children: React.ReactNode;
  isPortalRendered?: boolean;
}

const ModalWrapper = ({
  title, onModalClose, children, isPortalRendered,
}: ModalWrapperProps) => {
  // When rendered via portal, we need to manipulate the parent document's body
  const getTargetBody = () => {
    if (isPortalRendered && window.top && window.top !== window.self) {
      try {
        return window.top.document.body;
      } catch {
        // Cross-origin access failed, fall back to iframe body
        return document.body;
      }
    }
    return document.body;
  };

  const disableScroll = () => {
    const targetBody = getTargetBody();
    targetBody.style.overflow = 'hidden';
  };

  const enableScroll = () => {
    const targetBody = getTargetBody();
    targetBody.style.overflow = '';
  };

  const handleClose = () => {
    enableScroll();
    onModalClose?.();
  };

  useEffect(() => {
    disableScroll();

    return () => {
      enableScroll();
    };
  }, []);

  return (
    <KatModal
      className="kat-modal-learning-module"
      visible
      onClose={handleClose}
    >
      <span slot="title" className="text-wrap">{title}</span>
      {children}
    </KatModal>
  );
};

const LearningModuleModal = ({
  learningModuleId,
  onModalClose,
  parentId,
  parentType,
  unauthenticated,
  clientId,
  isPortalRendered,
}: LearningModuleModalProps) => {
  // Under PolyD migration T1, the LP response only has single-locale instanceMap.
  // Force a fresh GetModule call to get the full multi-locale instanceMap.
  const isPolyMigrationT1 = useAppSelector(
    (state) => selectIsWeblabInT1(state, WeblabId.NalandaPolyMigration),
  );

  const {
    learningModule, isLoading, isError, error,
  } = useLearningModule(learningModuleId, { forceRefetch: isPolyMigrationT1 });

  if (isLoading) {
    return (
      <ModalWrapper onModalClose={onModalClose} isPortalRendered={isPortalRendered}>
        <LoadingView />
      </ModalWrapper>
    );
  }

  if (isError) {
    return (
      <ModalWrapper onModalClose={onModalClose} isPortalRendered={isPortalRendered}>
        <ErrorView statusCode={getStatusCodeFromError(error)} />
      </ModalWrapper>
    );
  }

  // @todo: Our not-found implementation is currently a page and shouldn't be reused here as is.
  // Therefore, falling back to Error View for the time being.
  // This branch should never be hit in learning pathways today due to strict validation of existence in the
  // learning pathway implementation (previously would've been a fatal error that crashed the app, which it hasn't yet in prod)
  if (!learningModule) {
    return (
      <ModalWrapper onModalClose={onModalClose} isPortalRendered={isPortalRendered}>
        <ErrorView statusCode={getStatusCodeFromError(error)} />
      </ModalWrapper>
    );
  }

  return (
    <ParentEntityProvider parentId={parentId} parentType={parentType}>
      <ModalWrapper title={learningModule.title} onModalClose={onModalClose} isPortalRendered={isPortalRendered}>
        <LearningModuleContentView
          learningModule={learningModule}
          unauthenticated={unauthenticated}
          clientId={clientId}
        />
      </ModalWrapper>
    </ParentEntityProvider>
  );
};

export default LearningModuleModal;
